import type { ResolvedFigure } from '../model.js';
import { box, point, SceneBuilder, validateViewport } from '../scene-builder.js';
import { finishPrimitive, type PrimitiveSpec } from './contract.js';
import { d, fits, fittedSize, type Frame, frameFor, header, note, textWidth, TYPE, valueChip, wrapText } from './kit.js';

export interface LifecycleStage {
  id: string;
  label: string;
  /** The standing amount held in this stage, if it is known. */
  stock?: string;
  /** One factual clause about what happens here. */
  detail?: string;
}

export interface LifecycleFlux {
  id: string;
  from: string;
  to: string;
  label: string;
  /** The measured rate crossing this boundary. */
  quantity: number;
}

export interface LifecycleSpec extends PrimitiveSpec {
  family: 'primitive-lifecycle';
  view: 'schematic';
  subject: string;
  unit: string;
  /** Stages in cycle order. The last stage returns to the first. */
  stages: LifecycleStage[];
  /** Exactly one flux per stage, forming a single closed loop. */
  fluxes: LifecycleFlux[];
  /** What the closure means, in the author's words. */
  closure: string;
  activeStageId?: string;
  title: string;
  subtitle: string;
}

export const lifecycleStageId = (id: string): string => `lifecycle.stage.${id}`;
export const lifecycleFluxId = (id: string): string => `lifecycle.flux.${id}`;
export const LIFECYCLE_RETURN_ID = 'lifecycle.return';

interface Metrics {
  frame: Frame;
  railGutter: number;
  cardX: number;
  cardW: number;
  cardH: number;
  stageGap: number;
  top: number;
  bottom: number;
  footerY: number;
  required: number;
}

function metrics(spec: LifecycleSpec): Metrics {
  const frame = frameFor(spec.viewport.width, spec.viewport.height);
  const railGutter = 34;
  const cardX = frame.margin + railGutter;
  const cardW = frame.width - frame.margin - cardX;
  const cardH = 58;
  const stageGap = 46;
  const top = frame.top + 16;
  const bottom = top + spec.stages.length * cardH + (spec.stages.length - 1) * stageGap;
  const footerY = bottom + 46;
  return { frame, railGutter, cardX, cardW, cardH, stageGap, top, bottom, footerY, required: footerY + 16 };
}

/**
 * A cycle is a claim that nothing leaves.
 *
 * The difference between a lifecycle and a pipeline is closure: the last stage
 * feeds the first, so in a steady state every stage gives back exactly what it
 * receives. That is a checkable claim, and this compiler checks it — the fluxes
 * must form one loop that visits every stage, and each stage's inflow must
 * equal its outflow — then draws the return path as a rail rather than as one
 * more arrow, because the return is what makes the figure a cycle at all.
 */
export function compileLifecycle(spec: LifecycleSpec): ResolvedFigure {
  validateLifecycle(spec);

  const builder = new SceneBuilder();
  const m = metrics(spec);
  const { frame } = m;
  const warnings: string[] = [];
  const fluxByFrom = new Map(spec.fluxes.map((flux) => [flux.from, flux]));
  const rate = spec.fluxes[0]!.quantity;
  const uniform = spec.fluxes.every((flux) => flux.quantity === rate);

  header(builder, frame, 'lifecycle', `CLOSED CYCLE · ${spec.subject.toUpperCase()}`, [
    uniform ? `${format(rate)} ${spec.unit} CROSSES EVERY BOUNDARY` : `${spec.stages.length} STAGES · EVERY STAGE BALANCES`,
    `${spec.stages.length} STAGES · IN EQUALS OUT`,
  ]);

  spec.stages.forEach((stage, index) => {
    const semanticId = lifecycleStageId(stage.id);
    const y = m.top + index * (m.cardH + m.stageGap);
    const bounds = box(m.cardX, y, m.cardW, m.cardH);
    const active = stage.id === spec.activeStageId;
    builder.rect(`${semanticId}.card`, semanticId, active ? 'active' : 'surface', bounds, {
      radius: 4,
      label: `${stage.label}${stage.stock ? `: holds ${stage.stock}` : ''}`,
      interactive: true,
    });
    builder.text(`${semanticId}.label`, semanticId, 'structure', point(m.cardX + 12, y + 24), stage.label.toUpperCase(), {
      size: fittedSize(stage.label.toUpperCase(), TYPE.heading, m.cardW - 130, true),
      mono: true,
      weight: 700,
    });
    // The stock caption is a caption, not content: it yields to a long detail
    // rather than colliding with it.
    const captionWidth = textWidth('HELD HERE', TYPE.micro, true, true);
    const detailRoom = m.cardW - 24 - (stage.stock ? captionWidth + 14 : 0);
    const captionFits = stage.stock !== undefined && (!stage.detail || fits(stage.detail, TYPE.micro, detailRoom, true));
    if (stage.detail) {
      builder.text(`${semanticId}.detail`, semanticId, 'annotation', point(m.cardX + 12, y + 42), stage.detail, {
        size: TYPE.micro,
        mono: true,
        weight: 500,
      });
    }
    if (stage.stock) {
      valueChip(builder, semanticId, `${semanticId}.stock`, point(m.cardX + m.cardW - 12, y + 20), stage.stock, {
        role: active ? 'active' : 'slot',
        align: 'end',
      });
      if (captionFits) {
        builder.text(`${semanticId}.stock.caption`, semanticId, 'annotation', point(m.cardX + m.cardW - 12, y + 46), 'HELD HERE', {
          size: TYPE.micro,
          mono: true,
          weight: 700,
          align: 'end',
          uppercase: true,
        });
      }
    }
    builder.anchor(`${semanticId}.in`, semanticId, point(m.cardX + m.cardW / 2, y), 'up');
    builder.anchor(`${semanticId}.out`, semanticId, point(m.cardX + m.cardW / 2, y + m.cardH), 'down');
    builder.hit(semanticId, bounds);

    const flux = fluxByFrom.get(stage.id)!;
    const balance = balanceOf(spec, stage.id);
    builder.relation(
      `${semanticId}.balance`,
      'steady-state',
      semanticId,
      lifecycleFluxId(flux.id),
      `${stage.label} receives ${format(balance.inflow)} and returns ${format(balance.outflow)} ${spec.unit}`,
    );

    // The forward flux to the next stage, drawn in the gap below the card.
    const isLast = index === spec.stages.length - 1;
    if (!isLast) {
      const fluxSemantic = lifecycleFluxId(flux.id);
      const x = m.cardX + m.cardW / 2;
      const fromY = y + m.cardH;
      const toY = y + m.cardH + m.stageGap;
      builder.line(`${fluxSemantic}.arrow`, fluxSemantic, 'operator', point(x, fromY + 2), point(x, toY - 2), { markerEnd: true });
      const midY = (fromY + toY) / 2;
      valueChip(builder, fluxSemantic, `${fluxSemantic}.value`, point(m.cardX + m.cardW, midY), `${format(flux.quantity)} ${spec.unit}`, {
        role: 'slot',
        align: 'end',
      });
      builder.text(`${fluxSemantic}.label`, fluxSemantic, 'annotation', point(m.cardX, midY + 4), flux.label.toUpperCase(), {
        size: TYPE.micro,
        mono: true,
        weight: 700,
        uppercase: true,
      });
      builder.anchor(`${fluxSemantic}.tail`, fluxSemantic, point(x, fromY), 'down');
      builder.anchor(`${fluxSemantic}.head`, fluxSemantic, point(x, toY), 'down');
      builder.hit(fluxSemantic, box(m.cardX, fromY, m.cardW, m.stageGap));
      builder.relation(`${fluxSemantic}.carries`, 'flows-to', semanticId, lifecycleStageId(flux.to), `${flux.label}: ${format(flux.quantity)} ${spec.unit}`);
    }
  });

  // The return: not one more arrow, but the rail that closes the figure.
  const returnFlux = fluxByFrom.get(spec.stages[spec.stages.length - 1]!.id)!;
  const railX = frame.margin + 12;
  const startY = m.bottom;
  const endY = m.top;
  const dropY = startY + 22;
  const riseY = endY - 22;
  builder.path(`${LIFECYCLE_RETURN_ID}.rail`, LIFECYCLE_RETURN_ID, 'operator',
    `M ${d(m.cardX + m.cardW / 2)} ${d(startY)} L ${d(m.cardX + m.cardW / 2)} ${d(dropY)} L ${d(railX)} ${d(dropY)} L ${d(railX)} ${d(riseY)} L ${d(m.cardX + m.cardW / 2)} ${d(riseY)} L ${d(m.cardX + m.cardW / 2)} ${d(endY)}`,
    { markerEnd: true },
  );
  builder.text(`${LIFECYCLE_RETURN_ID}.label`, LIFECYCLE_RETURN_ID, 'annotation', point(railX - 6, (dropY + riseY) / 2), `${returnFlux.label.toUpperCase()} · ${format(returnFlux.quantity)} ${spec.unit}`, {
    size: TYPE.micro,
    mono: true,
    weight: 700,
    align: 'middle',
    rotate: -90,
    uppercase: true,
  });
  builder.anchor(`${LIFECYCLE_RETURN_ID}.tail`, LIFECYCLE_RETURN_ID, point(m.cardX + m.cardW / 2, startY), 'down');
  builder.anchor(`${LIFECYCLE_RETURN_ID}.head`, LIFECYCLE_RETURN_ID, point(m.cardX + m.cardW / 2, endY), 'up');
  builder.hit(LIFECYCLE_RETURN_ID, box(frame.margin, riseY, m.railGutter, dropY - riseY));
  builder.relation(`${LIFECYCLE_RETURN_ID}.closes`, 'closes-cycle', lifecycleStageId(returnFlux.from), lifecycleStageId(returnFlux.to), `${returnFlux.label} returns ${format(returnFlux.quantity)} ${spec.unit} and closes the cycle`);

  const closureLines = wrapText(spec.closure.toUpperCase(), TYPE.micro, frame.contentWidth, true);
  closureLines.forEach((line, index) => {
    note(builder, `lifecycle.closure.${index}`, 'lifecycle', point(frame.margin, m.footerY + index * 14), line);
  });
  if (closureLines.length > 2) warnings.push('The closure statement needs more than two lines at this width.');

  return finishPrimitive(spec, builder, {
    title: spec.title,
    subtitle: spec.subtitle,
    state: spec.activeStageId ? `focus:${spec.activeStageId}` : 'overview',
    active: [spec.activeStageId ? lifecycleStageId(spec.activeStageId) : LIFECYCLE_RETURN_ID, ...(spec.emphasis ?? [])],
    warnings,
  });
}

export function validateLifecycle(spec: LifecycleSpec): void {
  validateViewport(spec.viewport.width, spec.viewport.height);
  if (spec.family !== 'primitive-lifecycle' || spec.view !== 'schematic') throw new Error('lifecycle: family and view are fixed');
  if (!spec.provenance.trim()) throw new Error('lifecycle: provenance is required');
  if (!spec.subject.trim() || !spec.unit.trim() || !spec.closure.trim()) throw new Error('lifecycle: subject, unit and closure are required');
  if (!spec.title.trim() || !spec.subtitle.trim()) throw new Error('lifecycle: title and subtitle are required');
  if (spec.stages.length < 3 || spec.stages.length > 5) throw new Error('lifecycle: require 3…5 stages');
  const ids = new Set<string>();
  for (const stage of spec.stages) {
    if (!/^[a-z][a-z0-9-]*$/.test(stage.id)) throw new Error(`lifecycle: invalid stage id ${stage.id}`);
    if (ids.has(stage.id)) throw new Error(`lifecycle: duplicate stage id ${stage.id}`);
    ids.add(stage.id);
    if (!stage.label.trim() || stage.label.length > 18) throw new Error(`lifecycle: stage ${stage.id} needs a label of 1…18 characters`);
  }
  if (spec.fluxes.length !== spec.stages.length) throw new Error('lifecycle: require exactly one outgoing flux per stage');
  const fluxIds = new Set<string>();
  const outgoing = new Map<string, string>();
  for (const flux of spec.fluxes) {
    if (!/^[a-z][a-z0-9-]*$/.test(flux.id)) throw new Error(`lifecycle: invalid flux id ${flux.id}`);
    if (fluxIds.has(flux.id)) throw new Error(`lifecycle: duplicate flux id ${flux.id}`);
    fluxIds.add(flux.id);
    if (!ids.has(flux.from) || !ids.has(flux.to)) throw new Error(`lifecycle: flux ${flux.id} names a stage that does not exist`);
    if (flux.from === flux.to) throw new Error(`lifecycle: flux ${flux.id} cannot leave and enter the same stage`);
    if (outgoing.has(flux.from)) throw new Error(`lifecycle: stage ${flux.from} has more than one outgoing flux`);
    if (!Number.isFinite(flux.quantity) || flux.quantity <= 0) throw new Error(`lifecycle: flux ${flux.id} needs a positive measured rate`);
    if (!flux.label.trim() || flux.label.length > 22) throw new Error(`lifecycle: flux ${flux.id} needs a label of 1…22 characters`);
    outgoing.set(flux.from, flux.to);
  }

  // One loop that visits every stage exactly once, in the declared order.
  let cursor = spec.stages[0]!.id;
  for (let step = 0; step < spec.stages.length; step += 1) {
    const expected = spec.stages[(step + 1) % spec.stages.length]!.id;
    const next = outgoing.get(cursor);
    if (next !== expected) throw new Error('lifecycle: the fluxes must form one closed loop in stage order');
    cursor = next;
  }
  if (cursor !== spec.stages[0]!.id) throw new Error('lifecycle: the cycle does not return to its first stage');

  for (const stage of spec.stages) {
    const balance = balanceOf(spec, stage.id);
    if (balance.inflow !== balance.outflow) {
      throw new Error(`lifecycle: ${stage.id} receives ${balance.inflow} and returns ${balance.outflow}; a cycle in steady state must balance`);
    }
  }
  if (spec.activeStageId && !ids.has(spec.activeStageId)) throw new Error('lifecycle: the active stage does not exist');
  const required = metrics(spec).required;
  if (spec.viewport.height < required) throw new Error(`lifecycle: this cycle needs at least ${Math.ceil(required)}px of height`);
}

function balanceOf(spec: LifecycleSpec, stageId: string): { inflow: number; outflow: number } {
  return {
    inflow: spec.fluxes.filter((flux) => flux.to === stageId).reduce((sum, flux) => sum + flux.quantity, 0),
    outflow: spec.fluxes.filter((flux) => flux.from === stageId).reduce((sum, flux) => sum + flux.quantity, 0),
  };
}

function format(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100);
}
